/**
 * Génération et encodage des identifiants et des clés.
 *
 * Le `roomId` et la clé AES sont **générés côté client**. Le serveur ne les invente pas :
 * il accepte le `roomId` s'il est libre. Cela garantit que le serveur ne peut pas
 * énumérer les salles ni deviner une clé.
 *
 * Le module n'utilise que `crypto.getRandomValues` et `btoa`/`atob`, disponibles
 * nativement dans les navigateurs modernes et dans Node ≥ 16. Aucune dépendance Node.
 */

import { KEY_BYTES, ROOM_ID_LENGTH } from './constants.js';
import type { Bytes } from './types.js';

const BASE62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

/**
 * Génère une chaîne aléatoire en base62.
 *
 * Utilise le rejet d'échantillonnage pour éviter le biais modulo : sans lui, les
 * premiers caractères de l'alphabet seraient légèrement plus probables.
 */
export function randomBase62(length: number): string {
  if (length <= 0) return '';
  // 62 ne divise pas 256 : on rejette les octets au-dessus du plus grand multiple de 62.
  const limit = Math.floor(256 / BASE62.length) * BASE62.length;
  const out: string[] = [];
  while (out.length < length) {
    const buf = new Uint8Array(length * 2);
    crypto.getRandomValues(buf);
    for (const byte of buf) {
      if (byte >= limit) continue;
      const char = BASE62[byte % BASE62.length];
      if (char === undefined) continue;
      out.push(char);
      if (out.length === length) break;
    }
  }
  return out.join('');
}

/** Génère un identifiant de salle (21 caractères base62 ≈ 125 bits). */
export function randomRoomId(): string {
  return randomBase62(ROOM_ID_LENGTH);
}

/** Génère un identifiant de pair, utilisé pour router les messages dans une salle. */
export function randomPeerId(): string {
  return randomBase62(16);
}

/** Génère une clé AES-GCM de 256 bits. */
export function generateKeyBytes(): Bytes {
  const key = new Uint8Array(KEY_BYTES);
  crypto.getRandomValues(key);
  return key;
}

/** Encode des octets en base64url (sans padding), utilisable dans un fragment d'URL. */
export function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Décode une chaîne base64url. Renvoie `null` si l'entrée est invalide. */
export function fromBase64Url(value: string): Bytes | null {
  try {
    const padded = value.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

/** Préfixe du fragment d'URL transportant la clé. */
export const KEY_FRAGMENT_PREFIX = 'k=';

/**
 * Extrait la clé de partage d'un fragment d'URL (`#k=<base64url>`).
 *
 * Le fragment n'est **jamais** envoyé au serveur par le navigateur : c'est le mécanisme
 * qui rend le chiffrement à connaissance nulle possible sans échange de clé.
 *
 * Renvoie `null` si le fragment est absent, malformé, ou de mauvaise longueur.
 */
export function parseKeyFromFragment(hash: string): Bytes | null {
  const cleaned = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(cleaned);
  const raw = params.get('k');
  if (!raw) return null;
  const bytes = fromBase64Url(raw);
  if (!bytes || bytes.length !== KEY_BYTES) return null;
  return bytes;
}

/** Construit le fragment d'URL à partir d'une clé. */
export function buildKeyFragment(key: Uint8Array): string {
  return `#${KEY_FRAGMENT_PREFIX}${toBase64Url(key)}`;
}

/** Construit le lien de partage complet. */
export function buildShareUrl(baseUrl: string, roomId: string, key: Uint8Array): string {
  return `${baseUrl.replace(/\/$/, '')}/t/${roomId}${buildKeyFragment(key)}`;
}
